import { filterObjectProps, convertEmptyStringsToNull } from "@shared/modulePayload";
import type {
	MinorSeason,
	MinorSeasonT2,
	ModuleFull,
	PerennialCroplandPayload,
	PerennialCroplandsResponse,
	PerennialCroplandT2,
} from "@features/modules/perennialCropland/types";
import { FEModulesObj } from "../../../utils/moduleList";
import { FEModules } from "../../../utils/moduleList";
import { apiSlice, publicApiSlice, TagTypes } from "../../apiSlice";
import { createModuleGetApi, createModuleUpdateApi } from "./factories/createModuleApi";

const includedPropList = [
	"id",
	"status",
	"area",
	"is_biomass_burned_start",
	"is_biomass_burned_w",
	"is_biomass_burned_wo",
	"crop_yield_t2_start",
	"crop_yield_t2_w",
	"crop_yield_t2_wo",
	"land_use_type_start",
	"land_use_type_w",
	"land_use_type_wo",
	"tillage_management_type_start",
	"tillage_management_type_w",
	"tillage_management_type_wo",
	"organic_input_type_start",
	"organic_input_type_w",
	"organic_input_type_wo",
	"land_use_type_thread",
	"tillage_management_type_thread",
	"organic_input_type_thread",
	"is_biomass_burned_thread",
	"crop_yield_t2_thread",
	"land_use_change",
	"activity",
	"minorSeason",
	"note",
	"is_complete_renewal_start",
	"is_complete_renewal_w",
	"is_complete_renewal_wo",
	"is_system_in_maturity",
];
const minorSeasonPropsList = [
	"minor_land_use_type_start",
	"minor_land_use_type_w",
	"minor_land_use_type_wo",
	"minor_residue_management_type_start",
	"minor_residue_management_type_w",
	"minor_residue_management_type_wo",
	"minor_yield_start",
	"minor_yield_w",
	"minor_yield_wo",
];
const includedT2PropList = [
	"soc_t2_start",
	"soc_t2_w",
	"soc_t2_wo",
	"flu_t2_start",
	"flu_t2_w",
	"flu_t2_wo",
	"tillage_factor_t2_start",
	"tillage_factor_t2_w",
	"tillage_factor_t2_wo",
	"agb_t2_start",
	"agb_t2_w",
	"agb_t2_wo",
	"bgb_t2_start",
	"bgb_t2_w",
	"bgb_t2_wo",
	"fi_t2_start",
	"fi_t2_w",
	"fi_t2_wo",
	"residue_availability_t2_start",
	"residue_availability_t2_w",
	"residue_availability_t2_wo",
	"fire_periodicity_t2_start",
	"fire_periodicity_t2_w",
	"fire_periodicity_t2_wo",
	"agb_max_t2_start",
	"agb_max_t2_w",
	"agb_max_t2_wo",
	"agb_rate_t2_start",
	"agb_rate_t2_w",
	"agb_rate_t2_wo",
	"fmg_t2_start",
	"fmg_t2_w",
	"fmg_t2_wo",
	"ag_t2_start",
	"ag_t2_w",
	"ag_t2_wo",
	"bg_t2_start",
	"bg_t2_w",
	"bg_t2_wo",
	"input_factor_t2_start",
	"input_factor_t2_w",
	"input_factor_t2_wo",
];

// Define constants for the module
const MODULE_TYPE = "PerennialCropland";
const TAG: TagTypes = "PerennialCropland";
const TAG_DEFAULTS: TagTypes = "PerennialCroplandDefaults";
const ENDPOINT = FEModulesObj[FEModules.PerennialCropland].path;
const t2PercentageProps = ["minor_biomass_factor_t2_start", "minor_biomass_factor_t2_w", "minor_biomass_factor_t2_wo"];

const transformResponse = (response: PerennialCroplandsResponse) => {
	if (response) {
		const moduleMinorSeasonData = filterObjectProps(
			response,
			minorSeasonPropsList,
		) as MinorSeason;

		const t2MinorSeasonData = filterObjectProps(response, t2PercentageProps) as MinorSeasonT2;

		const module = filterObjectProps(
			{ ...response, minorSeason: moduleMinorSeasonData },
			includedPropList,
		) as PerennialCroplandPayload;

		const t2 = filterObjectProps(
			{ ...response, minorSeason: t2MinorSeasonData },
			includedT2PropList,
		) as PerennialCroplandT2;
		return {
			module: module,
			tiertwo: t2,
		};
	}
	return null;
}

const perennialCroplandApiSlice = createModuleGetApi<ModuleFull>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformResponse,
})

const publicPerennialCroplandApiSlice = createModuleGetApi<ModuleFull>({
	apiSlice: publicApiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformResponse,
})

export const {
	useGetPerennialCroplandQuery,
	useGetPerennialCroplandDefaultsQuery
} = perennialCroplandApiSlice;

export const {
	useGetPerennialCroplandQuery: usePublicGetPerennialCroplandQuery,
	useGetPerennialCroplandDefaultsQuery: usePublicGetPerennialCroplandDefaultsQuery
} = publicPerennialCroplandApiSlice;

const transformUpdatePayload = (data: Partial<ModuleFull>, activityId: number) => {
	const module = { ...data.module /* ...data.module.minorSeason */ };
	const tiertwo = { ...data.tiertwo /* ...data.tiertwo.minorSeason */ };
	const lucId = data?.module?.land_use_change;

	const payload = {
		...module,
		...tiertwo,
	} as unknown as PerennialCroplandsResponse;
	const { id, ...rest } = payload;
	const body = { ...rest, activity: activityId, land_use_change: lucId };

	convertEmptyStringsToNull(body);
	return { body, id };
}

const mutationApiSlice = createModuleUpdateApi<ModuleFull, PerennialCroplandsResponse>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformUpdatePayload,
	includesLandUseChange: true,
})

export const {
	useUpdatePerennialCroplandMutation,
} = mutationApiSlice;

export const perennialCroplandEndpoints = perennialCroplandApiSlice.endpoints;
export const perennialCroplandMutationEndpoints = mutationApiSlice.endpoints;